import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import styles from '../simulator-v2-create-new-journey/simulator-v2-create-new-journey.module.scss';
import 'dayjs/locale/en'; // Replace 'en' with the desired locale
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  CcfDatePickerIcon,
  CcfTextField,
  CcfBox,
  CcfTypography,
  CcfButton,
} from '@nice-ccf/ui-controls';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useState } from 'react';

/**
 * used to show the External Data Tab
 * @param props - SimulatorV2ExternalDataTabProps
 * @example ` <SimulatorV2ExternalDataTab />`
 */
const DatePickerIcon = () => {
  return (
    <CcfBox sx={{ display: 'flex' }}>
      <CcfDatePickerIcon />
    </CcfBox>
  );
};

/**
 * used to show the External Data Tab
 * @param props - SimulatorV2ExternalDataTabProps
 * @example ` <SimulatorV2ExternalDataTab />`
 */
const EditDialogBox: React.FC<{
  isOpen: boolean;
  handleClose: () => void;
  modalHeader: string;
  modalData: any;
  modalDataType: string;
  onSave: (data: any, type: any) => void;
  notRequiredFields?: string[];
}> = ({
  isOpen,
  handleClose,
  modalHeader,
  modalData,
  modalDataType,
  onSave,
  notRequiredFields,
}) => {
  const locale = 'en'; // Set the locale explicitly
  dayjs.locale(locale); // Set the locale globally
  const [record, setRecord] = useState(modalData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  return (
    <CcfBox>
      <Dialog
        onClose={handleClose}
        open={isOpen}
        maxWidth="md"
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <DialogTitle sx={{ fontWeight: '600', fontSize: '20px' }}>
          {modalHeader}
        </DialogTitle>
        <DialogContent className={styles.blockContainer}>
          <CcfBox>
            {Object.keys(record).map(
              (item, index) =>
                !notRequiredFields?.includes(item) && (
                  <CcfBox className={styles.suggestionCard}>
                    <CcfBox className={styles.containerGrid}>
                      <CcfTypography className={styles.inputLabel}>
                        {item.toUpperCase()}{' '}
                        <span style={{ color: '#C2041A' }}>*</span>
                      </CcfTypography>
                      {item === 'label' && (
                        <CcfTypography
                          style={{ fontSize: '11px', color: '#C2041A' }}
                        >
                          Use commas to separate id and value
                        </CcfTypography>
                      )}
                      {item !== 'date' ? (
                        <CcfTextField
                          id={`textfield-${item}`} // Added id property
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                              height: '36px',
                              border: '1px solid #AFC1CE',
                            },
                          }}
                          style={{ marginBottom: '15px' }}
                          InputLabelProps={{ shrink: true }}
                          value={record[item] || ''}
                          error={!!errors[`${item}`]}
                          helperText={errors[`${item}`]}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const newErrors = { ...errors };
                            if (!event.target.value) {
                              newErrors[item] = `${item} is required`;
                            } else {
                              delete newErrors[item];
                            }
                            setErrors(newErrors);

                            setRecord((prevRecord: Record<string, any>) => ({
                              ...prevRecord,
                              [item]: event.target.value,
                            }));
                          }}
                        />
                      ) : (
                        <LocalizationProvider
                          adapterLocale={locale}
                          dateAdapter={AdapterDayjs}
                        >
                          <DateTimePicker
                            value={record[item] ? dayjs(record[item]) : null}
                            onChange={(newValue) =>
                              setRecord((prevRecord: any) => ({
                                ...prevRecord,
                                [item]: newValue?.toISOString(),
                              }))
                            }
                            renderInput={(props) => (
                              <CcfTextField
                                id={`textfield-${item}`} // Added id property
                                variant="outlined"
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                    height: '36px',
                                    border: '1px solid #AFC1CE',
                                  },
                                }}
                                style={{ marginBottom: '15px' }}
                                InputLabelProps={{ shrink: true }}
                                {...props}
                              />
                            )}
                            components={{
                              OpenPickerIcon: DatePickerIcon,
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    </CcfBox>
                  </CcfBox>
                )
            )}
          </CcfBox>
        </DialogContent>
        <DialogActions>
          <CcfButton onClick={handleClose}>
            <CcfTypography>Cancel</CcfTypography>
          </CcfButton>
          <CcfButton
            onClick={() => {
              onSave(record, 'edit');
            }}
            primary
            disabled={errors && Object.keys(errors).length > 0}
          >
            <CcfTypography>Save</CcfTypography>
          </CcfButton>
        </DialogActions>
      </Dialog>
    </CcfBox>
  );
};

export default EditDialogBox;
